import dayjs from "dayjs";
import type { ConfigType, Dayjs } from "dayjs";

export const US_DATE_FORMAT = "MM/DD/YYYY";

const US_DATE_TIME_FORMAT = "MM/DD/YYYY HH:mm:ss";
const LABEL_NEVER = "Never";
const ISO_DATE_ONLY_LENGTH = 10;
const PAD_LENGTH = 2;

type DateInput = string | number | Date | Dayjs;

type DateParams = [string, string, string];

export const getFormattedIsoDateTime = (isoDateTime?: ConfigType): string => {
  const formatted = isoDateTime
    ? dayjs(isoDateTime).format(US_DATE_TIME_FORMAT)
    : LABEL_NEVER;

  return formatted;
};

const pad = (value: number): string => String(value).padStart(PAD_LENGTH, "0");

const getDateParamsFromDate = (date: Date): DateParams => {
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = String(date.getFullYear());

  return [ month, day, year ];
};

// A dayjs object also works here, because the Date constructor reads its
// numeric value, the same as the JavaScript version did.
const toDate = (value: DateInput): Date => {
  const date = new Date(value as string | number | Date);

  return date;
};

const isDateOnlyString = (value: DateInput): boolean => {
  const isString = typeof value === "string";
  const hasDateOnlyLength = isString && value.length === ISO_DATE_ONLY_LENGTH;

  return hasDateOnlyLength;
};

export const getIsoDateFromDate = (date: Date): string => {
  const [ month, day, year ] = getDateParamsFromDate(date);

  return [ year, month, day ].join("-");
};

const getUsDateFromIsoDateOnlyString = (isoDate: string): string => {
  const [ year, month, day ] = isoDate.split("-");

  return [ month, day, year ].join("/");
};

export const getUsDateFromDate = (isoDate?: DateInput): string => {
  if (!isoDate) {
    return "";
  }

  const isDateOnly = isDateOnlyString(isoDate);

  if (isDateOnly) {
    return getUsDateFromIsoDateOnlyString(isoDate as string);
  }

  const date = toDate(isoDate);

  const [ month, day, year ] = getDateParamsFromDate(date);

  return [ month, day, year ].join("/");
};

export const getUsDateFromIsoDate = (isoDate?: DateInput): string => {
  if (!isoDate) {
    return "";
  }

  const isDateOnly = isDateOnlyString(isoDate);

  if (isDateOnly) {
    return getUsDateFromIsoDateOnlyString(isoDate as string);
  }

  const date = toDate(isoDate);

  return getUsDateFromDate(date);
};

export const getIsoDateFromStringDate = (
  date: string | number | Date,
  shouldIncludeTime = false,
): string => {
  const dateTime = new Date(date);

  if (shouldIncludeTime) {
    return dateTime.toISOString();
  }

  const [ month, day, year ] = getDateParamsFromDate(dateTime);

  return [ year, month, day ].join("-");
};

export const today = (): string => {
  const formatted = dayjs().format(US_DATE_FORMAT);

  return formatted;
};
